import {useMemo} from 'react';

import useMoleculeSchemasPairing from 'chemistry/bulk-upload/useMoleculeSchemasPairing';
import type {SchemaPairing} from 'chemistry/bulk-upload/useMoleculeSchemasPairing';

export enum MismatchStatus {
  OK = 'ok',
  BLOCKED = 'blocked',
}

export enum SelectedSchemaRole {
  FORM = 'form',
  COMPOUND_WITH_FORM = 'compound_with_form',
}

export type SchemaMismatchResult =
  | {
      status: MismatchStatus.OK;
      linkedSchemaName: string | null;
      selectedSchemaRole: SelectedSchemaRole | null;
    }
  | {status: MismatchStatus.BLOCKED; message: string};

interface UseSchemaMismatchDetectionParams {
  tagSchemaId: string | null;
  hasCounterparts: boolean;
  registryId: string | null;
}

function getSchemaRole(schemaId: string, pairing: SchemaPairing | undefined): SelectedSchemaRole | null {
  if (!pairing) {
    return null;
  }
  if (pairing.formSchemaId === schemaId) {
    return SelectedSchemaRole.FORM;
  }
  if (pairing.compoundSchemaId === schemaId) {
    return SelectedSchemaRole.COMPOUND_WITH_FORM;
  }
  return null;
}

export default function useSchemaMismatchDetection({
  tagSchemaId,
  hasCounterparts,
  registryId,
}: UseSchemaMismatchDetectionParams): SchemaMismatchResult {
  const {pairingMap, isLoading} = useMoleculeSchemasPairing(registryId);

  return useMemo((): SchemaMismatchResult => {
    if (isLoading || tagSchemaId == null) {
      return {status: MismatchStatus.OK, linkedSchemaName: null, selectedSchemaRole: null};
    }

    const pairing = pairingMap[tagSchemaId];
    const selectedSchemaRole = getSchemaRole(tagSchemaId, pairing);

    if (selectedSchemaRole === SelectedSchemaRole.FORM) {
      return {status: MismatchStatus.OK, linkedSchemaName: pairing.compoundSchemaName, selectedSchemaRole};
    }
    if (selectedSchemaRole === SelectedSchemaRole.COMPOUND_WITH_FORM) {
      return {status: MismatchStatus.OK, linkedSchemaName: pairing.formSchemaName, selectedSchemaRole};
    }

    if (hasCounterparts) {
      return {
        status: MismatchStatus.BLOCKED,
        message:
          'This structure contains salts or solvents, but the selected schema has no linked Molecule Form schema to support them. Select a schema with a linked Form, or simplify the structure.',
      };
    }

    return {status: MismatchStatus.OK, linkedSchemaName: null, selectedSchemaRole};
  }, [isLoading, tagSchemaId, pairingMap, hasCounterparts]);
}
